// ====================================================================
// NET — resilience helpers for flaky, high-latency school Wi-Fi
// ====================================================================
// Core-safe: no THREE, no DOM, no build flags — safe to import on the no-build
// page, in dev, and in the kid build (matches the gltf.js / bakedWorld.js core
// contract). Everything here runs at LOAD time, never per-frame, so the
// allocation of AbortControllers / chunk arrays is not a hot-path concern.
//
// The network we harden for drops packets, stalls mid-transfer (TCP up, no
// bytes), and has high RTT. The primitives below add the two things plain
// fetch()/GLTFLoader.load() lack on that network: a hard per-attempt TIMEOUT
// (so a stalled request can't hang forever) and automatic RETRY with backoff
// (so a transient blip self-heals instead of surfacing a hard failure to a kid).

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// fetch() with a per-attempt AbortController timeout and exponential backoff.
// A non-ok status is thrown so it counts as a retryable failure. Rejects only
// after every attempt (1 + retries) is exhausted; the last error propagates.
export async function fetchWithRetry(url, opts = {}) {
  const { timeoutMs = 10000, retries = 2, backoffMs = 500, fetchOpts = {} } = opts;
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(url, { ...fetchOpts, signal: ctrl.signal });
      clearTimeout(timer);
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
      return res;
    } catch (err) {
      clearTimeout(timer);
      lastErr = err;
      // Back off before the next try: 500, 1000, 2000, … ms.
      if (attempt < retries) await sleep(backoffMs * (2 ** attempt));
    }
  }
  throw lastErr;
}

function concatChunks(chunks, len) {
  const out = new Uint8Array(len);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}

// Load a .glb with timeout + retry + byte progress. GLTFLoader.load() can't be
// aborted and exposes no timeout hook, so instead of calling it we fetch the
// bytes ourselves (via fetchWithRetry — timeout, retry, real progress) and hand
// the buffer to loader.parse(). parse() runs the identical GLTF pipeline,
// INCLUDING the meshopt decoder attached by makeGLTFLoader(), so a future
// meshopt-packed asset decodes here unchanged.
//
// onProgress (optional) receives { loaded, total, lengthComputable } as bytes
// stream in. total/lengthComputable are 0/false when the server sends no
// Content-Length (common), so callers must handle the indeterminate case.
export async function loadGLBWithRetry(loader, url, opts = {}) {
  const { onProgress, retries = 2, timeoutMs = 12000, backoffMs = 500 } = opts;
  const res = await fetchWithRetry(url, { timeoutMs, retries, backoffMs });

  const total = Number(res.headers.get('content-length')) || 0;
  const reader = res.body && res.body.getReader ? res.body.getReader() : null;

  let buf;
  if (reader) {
    // Stream so we can report progress as bytes arrive.
    let loaded = 0;
    const chunks = [];
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      if (onProgress) onProgress({ loaded, total, lengthComputable: total > 0 });
    }
    buf = concatChunks(chunks, loaded).buffer;
  } else {
    // Defensive fallback for any runtime without a readable body stream: we
    // still get the buffer, just without progress ticks.
    buf = await res.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    // Empty path arg is correct: our .glb assets are self-contained binaries
    // (no external .bin or texture files to resolve against a base URL).
    loader.parse(buf, '', resolve, reject);
  });
}
